import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useI18n, LOCALE_CONFIG } from "@/i18n";
import type { Locale } from "@/i18n";

export function LanguageSwitcher() {
  const { locale, setLocale } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const autoOpenDone = useRef(false);

  // Auto-open on first visit (once per session)
  useEffect(() => {
    if (autoOpenDone.current) return;
    const shown = sessionStorage.getItem("kra-guide-lang-shown");
    if (shown) return;
    autoOpenDone.current = true;

    const openTimer = setTimeout(() => setOpen(true), 800);
    const closeTimer = setTimeout(() => {
      setOpen(false);
      sessionStorage.setItem("kra-guide-lang-shown", "1");
    }, 5000);

    return () => {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
    };
  }, []);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const current = LOCALE_CONFIG[locale];

  return (
    <div ref={ref} className="relative z-50">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-2 rounded-full bg-white/80 backdrop-blur-md border border-surface-2 shadow-soft text-sm font-medium text-ink-1 transition-all hover:shadow-elevated hover:border-surface-3"
      >
        <span className="text-base">{current.flag}</span>
        <span className="hidden sm:inline">{current.name}</span>
        <svg
          className={`w-3.5 h-3.5 text-ink-3 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="absolute right-0 top-full mt-2 bg-white/95 backdrop-blur-xl rounded-2xl border border-surface-2 shadow-elevated overflow-hidden min-w-[160px]"
          >
            {(Object.entries(LOCALE_CONFIG) as [Locale, typeof current][]).map(
              ([code, config]) => (
                <button
                  key={code}
                  onClick={() => {
                    setLocale(code);
                    setOpen(false);
                    sessionStorage.setItem("kra-guide-lang-shown", "1");
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                    code === locale
                      ? "bg-kra-soft text-kra-dark font-medium"
                      : "text-ink-1 hover:bg-surface-1"
                  }`}
                >
                  <span className="text-base">{config.flag}</span>
                  <span>{config.name}</span>
                </button>
              )
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
